import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {FONT_PROXIMA_13} from 'styles/FontConstants';
import BioentityChip from 'ui-library/chips/BioentityChip';
import {ChipSize} from 'ui-library/chips/Chip';
import {GRAY_DARK, GRAY_DARKER, GRAY_LIGHT, WHITE} from 'ui-library/Colors';
import Spinner from 'ui-library/Spinner';

import {MAX_BULK_PREDICTION_ITEMS} from '../ScientificModelConstants';
import type {BulkPredictionItem} from './BulkPredictionUtils';

interface BulkPredictionItemsListProps {
  items: Array<BulkPredictionItem>;
  onRemoveItem: (id: string) => void;
  isLoading?: boolean;
}

export default function BulkPredictionItemsList({
  items,
  onRemoveItem,
  isLoading,
}: BulkPredictionItemsListProps): JSX.Element {
  return (
    <div className={css(styles.tableWrapper)}>
      <table className={css(styles.table)}>
        <thead>
          <tr className={css(styles.headerRow)}>
            <th className={css(styles.headerCell)}>
              <div className={css(styles.headerContent)}>
                <span>Input Entity</span>
                <span className={css(styles.countIndicator)}>
                  {items.length}/{MAX_BULK_PREDICTION_ITEMS}
                </span>
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          {isLoading ? (
            <tr>
              <td className={css(styles.loadingCell)}>
                <div className={css(styles.loadingContent)}>
                  <Spinner />
                  <span>Loading worklist items...</span>
                </div>
              </td>
            </tr>
          ) : items.length === 0 ? (
            <tr>
              <td className={css(styles.placeholderCell)}>
                No items added yet. Use the options above to add items.
              </td>
            </tr>
          ) : (
            items.map((item) => (
              <tr key={item.id} className={css(styles.bodyRow)}>
                <td className={css(styles.bodyCell)}>
                  <BioentityChip
                    id={item.id}
                    size={ChipSize.SMALL}
                    isLink={false}
                    onRemove={() => {
                      onRemoveItem(item.id);
                    }}
                  />
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

const styles = StyleSheet.create({
  tableWrapper: {
    flex: 1,
    overflowY: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  headerRow: {
    backgroundColor: WHITE,
    position: 'sticky',
    top: 0,
    zIndex: 1,
  },
  headerCell: {
    ...FONT_PROXIMA_13,
    color: GRAY_DARKER,
    fontWeight: 600,
    padding: '8px 12px',
    textAlign: 'left',
    borderBottom: `1px solid ${GRAY_LIGHT}`,
  },
  headerContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  countIndicator: {
    ...FONT_PROXIMA_13,
    color: GRAY_DARK,
    fontWeight: 400,
  },
  loadingCell: {
    ...FONT_PROXIMA_13,
    color: GRAY_DARK,
    padding: '24px 16px',
    textAlign: 'center',
  },
  loadingContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
  },
  placeholderCell: {
    ...FONT_PROXIMA_13,
    color: GRAY_DARK,
    padding: '32px 16px',
    textAlign: 'center',
  },
  bodyRow: {
    borderBottom: `1px solid ${GRAY_LIGHT}`,
    ':last-child': {
      borderBottom: 'none',
    },
  },
  bodyCell: {
    padding: '6px 12px',
  },
});
